import { Avatar, Text } from "../atoms"
import { MentoringDataType } from "@/types/mentoring"

const ScheduledListItem = ({ label, value }: { label: string; value: string | number }) => {
  return (
    <li className="flex items-center justify-between">
      <Text variant="body">{label}</Text>
      <Text variant="h5">{value}</Text>
    </li>
  )
}

interface MentoringsDashProps {
  data: any // Objeto 'meta' vindo da API
  onMentoringClick: (mentoring: MentoringDataType) => void
}

export const MentoringsDash = ({ data, onMentoringClick }: MentoringsDashProps) => {
  // Extraímos os dados do objeto 'meta' da API V2
  const nextMentorings = data?.mentoriasPendentes || []
  const mentoriasRealizadas = data?.mentoriasRealizadas || {
    inCompany: 0,
    online: 0,
    total: 0,
  }

  // Como o dash agora recebe tudo pronto, removemos o loading interno e os useEffects
  if (!data) {
    return (
      <Text
        variant="body"
        className="mt-8 text-center"
      >
        Carregando dados do painel...
      </Text>
    )
  }

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-12">
      {/* Próximas Mentorias (baseado em mentoriasPendentes) */}
      <div className="flex flex-col gap-5 rounded-[8px] border border-primary bg-dark-500 p-[20px] xl:col-span-8">
        <Text
          variant="button"
          className="text-primary"
        >
          Próximas Mentorias
        </Text>

        {nextMentorings.length === 0 ? (
          <Text
            variant="body"
            className="mt-8 text-center"
          >
            Nenhuma mentoria marcada
          </Text>
        ) : (
          <div className="flex justify-between gap-4">
            {nextMentorings.map((mentoring: any, index: number) => (
              <div
                key={mentoring.mentoriaId}
                className="flex flex-1 gap-4"
              >
                <NextMentoringsCard
                  mentoring={mentoring}
                  onMentoringClick={onMentoringClick}
                />
                {index < nextMentorings.length - 1 && <div className="w-[1px] bg-dark-400" />}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Realizadas (baseado em mentoriasRealizadas) */}
      <div className="flex flex-col gap-5 rounded-[8px] border border-primary bg-dark-500 p-[20px] xl:col-span-4">
        <Text
          variant="button"
          className="text-primary"
        >
          Realizadas esse ano
        </Text>

        <ul className="list-none">
          <ScheduledListItem
            label="In Company"
            value={mentoriasRealizadas.inCompany}
          />
          <ScheduledListItem
            label="Online"
            value={mentoriasRealizadas.online}
          />
          <hr className="my-2 border-dark-400" />
          <ScheduledListItem
            label="Total"
            value={mentoriasRealizadas.total}
          />
        </ul>
      </div>
    </div>
  )
}

const NextMentoringsCard = ({
  mentoring,
  onMentoringClick,
}: {
  mentoring: any
  onMentoringClick: (mentoring: any) => void
}) => {
  const formattedDate = (dateString: string) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("pt-BR")
  }

  return (
    <div className="flex flex-1 flex-col justify-between gap-4">
      <div>
        <Text
          variant="small"
          className="text-dark-300"
        >
          {formattedDate(mentoring.dataHoraEntrega)}
        </Text>
        <Text
          onClick={() => onMentoringClick(mentoring)}
          variant="body"
          className="cursor-pointer hover:text-primary hover:underline"
        >
          {mentoring.descricao.substring(0, 100) + (mentoring.descricao.length > 30 ? "..." : "")}
        </Text>
      </div>
      <div className="flex items-center gap-2">
        <Avatar
          size="sm"
          alt={mentoring.empFantasia}
          // Note: O novo JSON não trouxe explicitamente empLogoUrl no meta,
          // usando fallback ou mantendo a estrutura se existir
          src={mentoring.empFotoUrl}
        />
        <Text variant="small">{mentoring.empFantasia}</Text>
      </div>
    </div>
  )
}
